import React from 'react';
import classes from './CookieNotice.module.scss';
import Cookies from 'js-cookie';

import A from '../../atoms/A/A';
import CookieSettings from '../../organisms/CookieDialog/CookieSettings';
import { readSettingsFromCookies } from '../../templates/TrackingProvider/TrackingProvider';
import SuspenseWithIndicator from 'components/atoms/SuspenseWithIndicator/SuspendWithIndicator';
import CompanyInfoPage from 'components/templates/CompanyInfoPage/CompanyInfoPage';
import Table from 'components/atoms/Table/Table';

export interface Props {
  className?: string;
}

const CookieNotice: React.FC<Props> = () => {
  const settingsData = React.useMemo(() => readSettingsFromCookies(), []);
  const settingCookie = settingsData?.settings.includes('set');
  const trackingCookie = settingsData?.settings.includes('tra');
  const marketingCookie = settingsData?.settings.includes('mar');
  const socialCookie = settingsData?.settings.includes('soc');

  const updateCookies = (settings: string[]) => {
    const newSettingsData = {
      settings,
      consentDate: Date.now(),
    };
    Cookies.set('cookie-consent', JSON.stringify(newSettingsData), {
      secure: window.location.protocol === 'https:',
      expires: 365,
    });
    window.location.reload();
  };

  const handleToggleSettingCookie = (value: boolean) => {
    const settings = [
      value ? 'set' : null,
      trackingCookie && 'tra',
      marketingCookie && 'mar',
      socialCookie && 'soc',
    ].filter(Boolean);
    updateCookies(settings);
  };
  const handleToggleMarketingCookie = (value: boolean) => {
    const settings = [
      value ? 'mar' : null,
      trackingCookie && 'tra',
      settingCookie && 'set',
      socialCookie && 'soc',
    ].filter(Boolean);
    updateCookies(settings);
  };

  const handleToggleTrackingCookie = (value: boolean) => {
    const settings = [
      value ? 'tra' : null,
      marketingCookie && 'mar',
      settingCookie && 'set',
      socialCookie && 'soc',
    ].filter(Boolean);
    updateCookies(settings);
  };

  const handleToggleSocialCookie = (value: boolean) => {
    const settings = [
      value ? 'soc' : null,
      marketingCookie && 'mar',
      settingCookie && 'set',
      trackingCookie && 'tra',
    ].filter(Boolean);
    updateCookies(settings);
  };

  return (
    <CompanyInfoPage>
      <h1>Cookie Notice</h1>
      <p>This Cookie Notice was last updated on 14th February 2022</p>

      <section>
        <h2>1. Welcome</h2>
        <p>Qmee Limited (“we”, “us”, “our”) is committed to protecting your privacy.</p>
        <p>
          This Cookie Notice explains what cookies are, what types of cookies are placed on your
          device when you visit our website and how we use them. Please see our&nbsp;
          <A to="/privacy-notice">Privacy Notice</A>&nbsp;for further information on how we process
          your personal information.
        </p>
      </section>
      <section>
        <h2>2. What are cookies?</h2>
        <p>
          Cookies are small text files that are sent to or accessed from your web browser or your
          device&apos;s memory. A cookie typically contains the name of the domain (internet
          location) from which the cookie originated, the “lifetime” of the cookie (i.e., when it
          expires) and a randomly generated unique number or other identifiers. A cookie also may
          contain information about your device, such as user settings, browsing history and
          activities conducted while using our services.
        </p>
      </section>
      <section>
        <h2>3. Who can drop cookies?</h2>
        <strong>First-party cookies</strong>
        <p>First-party cookies are placed on your device directly by us.</p>

        <strong>Third-Party Cookies</strong>
        <p>Third-party cookies are placed on your device by our partners and service providers.</p>
      </section>
      <section>
        <h2>4. How long does a cookie stay on my device?</h2>
        <strong>Session Cookies</strong>
        <p>
          There are session cookies and persistent cookies. Session cookies only last until you
          close your browser. We use session cookies for a variety of reasons, including to learn
          more about your use of our website during one single browser session and to help you to
          use our website more efficiently.
        </p>
        <strong>Persistent Cookies</strong>
        <p>
          Persistent cookies have a longer lifespan and aren&apos;t automatically deleted when you
          close your browser. These types of cookies are primarily used to help you quickly sign-in
          to our website again and for analytical purposes.
        </p>

        <strong>Do you use other tracking technologies?</strong>
        <p>
          Other technologies such as web beacons (also called pixel tags or clear gifs), tracking
          URLs or software development kits (SDKs) can also be used. Web beacons are tiny graphics
          files that contain a unique identifier that enable us to recognise when someone has
          visited our service or opened an e-mail that we have sent them. Tracking URLs are custom
          generated links that help us understand where the traffic to our webpages comes from. SDKs
          are small pieces of code included in apps, which function like cookies and web beacons.
        </p>
      </section>
      <section>
        <h2>5. What do we use cookies (and other technologies) for?</h2>
        <p>
          Like most providers of online services, we use cookies to provide, secure and improve our
          services, including by remembering your preferences, recognizing you when you visit our
          website and personalizing and tailoring ads to your interests. To accomplish these
          purposes, we also may link information from cookies with other personal information we
          hold about you. When you visit our website, some or all of the following types of cookies
          may be set on your device.
        </p>

        <Table className={classes.cookieTable}>
          <thead>
            <tr>
              <th>Cookie Type</th>
              <th>What does it do?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Essential website cookies</td>
              <td>
                These cookies are strictly necessary to provide you with services available through
                our website and to use some of its features, such as access to secure areas.
              </td>
            </tr>
            <tr>
              <td>Analytics cookies</td>
              <td>
                These cookies help us understand how our website is being used, how effective
                marketing campaigns are, and help us customize and improve our websites for you.
              </td>
            </tr>
            <tr>
              <td>Advertising cookies</td>
              <td>
                These cookies are used to make advertising messages more relevant to you. They
                perform functions like preventing the same ad from continuously reappearing,
                ensuring that ads are properly displayed for advertisers, selecting advertisements
                that are based on your interests and measuring the number of ads displayed and their
                performance, such as how many people clicked on a given ad.
              </td>
            </tr>
            <tr>
              <td>Social networking cookies</td>
              <td>
                These cookies are used to enable you to share pages and content that you find
                interesting on our website through third-party social networking and other websites.
                These cookies may also be used for advertising purposes too.
              </td>
            </tr>
          </tbody>
        </Table>

        <Table className={classes.cookieTable}>
          <thead>
            <tr>
              <th>Cookie name</th>
              <th>Purpose</th>
              <th>First party cookie?</th>
              <th>Third party cookie</th>
              <th>Session or persistent cookie</th>
              <th>Cookie expiry period</th>
              <th>Is this cookie strictly essential?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>cookie-consent</td>
              <td>Used to remember your cookie consent preferences</td>
              <td>yes</td>
              <td>-</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>essential</td>
            </tr>
            <tr>
              <td>access-token</td>
              <td>Used to keep you loggeed in</td>
              <td>yes</td>
              <td>-</td>
              <td>persistent</td>
              <td>30 mins</td>
              <td>essential</td>
            </tr>
            <tr>
              <td>refresh-token</td>
              <td>Used to remember your cookie consent preferences</td>
              <td>yes</td>
              <td>-</td>
              <td>persistent</td>
              <td>1 month</td>
              <td>essential</td>
            </tr>
            <tr>
              <td>qm_extenstion</td>
              <td>Used to allow the browser extension to work</td>
              <td>yes</td>
              <td>-</td>
              <td>persistent</td>
              <td>1 month</td>
              <td>essential</td>
            </tr>
            <tr>
              <td>logged_in</td>
              <td>Used to tell if you are logged in</td>
              <td>yes</td>
              <td>-</td>
              <td>persistent</td>
              <td>1 month</td>
              <td>essential</td>
            </tr>
            <tr>
              <td>_gat</td>
              <td>Used to throttle requests to Google Analytics, increasing network efficiency</td>
              <td>yes</td>
              <td>Google</td>
              <td>session</td>
              <td>1 minute</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_ga</td>
              <td>Used to distinguish users during tracking</td>
              <td>yes</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_gcl_au</td>
              <td>Used to store and track conversions.</td>
              <td>yes</td>
              <td>Google</td>
              <td>persistent</td>
              <td>3 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>APISID</td>
              <td>Used to collect user information for videos hosted by YouTube.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SAPISID</td>
              <td>Used to collect user information for videos hosted by YouTube.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>HSID</td>
              <td>Used to collect user information for videos hosted by YouTube.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SSID</td>
              <td>Used to collect user information for videos hosted by YouTube.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SIDCC</td>
              <td>Used to link a unique customer number for cross-device insights and CRM data.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>NID</td>
              <td>
                Used by Google to display personalized advertisements on Google sites, based on
                recent searches and previous interactions.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>6 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SID</td>
              <td>
                Used by Google to display personalized advertisements on Google sites, based on
                recent searches and previous interactions.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>1P_JAR</td>
              <td>
                Used to collect information about how the end user uses the website and any
                advertising that the end user may have seen before visiting the said website.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>30 days</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>NID</td>
              <td>
                Used by Google to display personalized advertisements on Google sites, based on
                recent searches and previous interactions.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>6 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_gat</td>
              <td>Used to distinguish users during tracking</td>
              <td>yes</td>
              <td>Google</td>
              <td>persistent</td>
              <td>24 hours</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SEARCH_SAMESITE</td>
              <td>
                Used to prevent the browser from sending this cookie along with cross-site requests.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>6 Months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>CONSENT</td>
              <td>Used to track Google&apos;s global consent to cookie use.</td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>6 Months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>__Secure-1PSID</td>
              <td>
                Used for targeting purposes to build a profile of the website visitor&apos;s
                interests in order to show relevant & personalised Google advertising.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>__Secure-3PSIDCC</td>
              <td>
                Used for targeting purposes to build a profile of the website visitor&apos;s
                interests in order to show relevant & personalised Google advertising.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>__Secure-3PAPISID</td>
              <td>
                Used for building a profile of website visitor interests to show relevant and
                personalized ads through retargeting.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>__Secure-3PSID</td>
              <td>
                Used for building a profile of website visitor interests to show relevant and
                personalized ads through retargeting.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>__Secure-1PAPISID</td>
              <td>
                Used for targeting purposes to build a profile of the website visitor&apos;s
                interests in order to show relevant & personalised Google advertising.
              </td>
              <td>no</td>
              <td>Google</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjid</td>
              <td>Used to distinguish users during tracking</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjSessionUser</td>
              <td>Used to persist the Hotjar User ID, unique to that site on the browser.</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjSession</td>
              <td>Used to hold the current session data.</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjDonePolls</td>
              <td>
                Used to ensure that the same survey does not reappear if it has already been filled
                in.
              </td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjIncludedInPageviewSample</td>
              <td>Used to let Hotjar know whether a visitor is included in the data sampling</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>30 mins</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjAbsoluteSessionInProgress</td>
              <td>Used to detect the first pageview session of a visitor</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>30 mins</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_hjTLDTest</td>
              <td>Used to find the most generic path for Hotjar, for use across subdomains</td>
              <td>yes</td>
              <td>Hotjar</td>
              <td>persistent</td>
              <td>30 mins</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_fbp</td>
              <td>Used to store and track visits across websites</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>3 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_fr</td>
              <td>Used to provide ad delivery or retargeting</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>3 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>sb</td>
              <td>Used to store browser details</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>datar</td>
              <td>Used to provide fraud prevention</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>xs</td>
              <td>Used to store a unique session ID</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>3 months</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>wd</td>
              <td>Used to read screen resolution</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>1 week</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>dpr</td>
              <td>Used to read screen resolution</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>1 week</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>c_user</td>
              <td>Used to store a unique user ID</td>
              <td>yes</td>
              <td>Facebook</td>
              <td>persistent</td>
              <td>30 days</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_s</td>
              <td>Used to collect data on visitors behaviour and interaction</td>
              <td>no</td>
              <td>Branch</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>twis</td>
              <td>Used to collect data on visitors behaviour and interaction</td>
              <td>no</td>
              <td>Branch</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>bnc_returning_visitor</td>
              <td>Used to collect data on visitors behaviour and interaction</td>
              <td>no</td>
              <td>Branch</td>
              <td>persistent</td>
              <td>30 days</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>mbox</td>
              <td>Used to enable additional features for logged in Twitter users</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>twid</td>
              <td>Used to enable additional features for logged in Twitter users</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>auth_token</td>
              <td>Used to enable additional features for logged in Twitter users</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>ct0</td>
              <td>Used to track the sharing of content on Twitter</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>kdt</td>
              <td>Used to track the sharing of content on Twitter</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>dnt</td>
              <td>Used to track the sharing of content on Twitter</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>external_referer</td>
              <td>Used to collect information about the pages viewed</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>guest_id</td>
              <td>Used to identify and track the website visitor</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_gid</td>
              <td>Used to distinguish users</td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>personalization_id</td>
              <td>
                Used to measure the performance of advertising campaigns through Twitter, across
                different browsers and devices used by a visitor.
              </td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>ads_prefs</td>
              <td>
                Used to track visitor activity from our Twitter ads on our website, and also to
                allow users to share content from our websites.
              </td>
              <td>no</td>
              <td>Twitter</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_uetsid</td>
              <td>
                Used to collect information about pages viewed by the user, and to handle
                remarketing campaigns.
              </td>
              <td>yes</td>
              <td>Bing</td>
              <td>persistent</td>
              <td>1 day</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_uetvid</td>
              <td>Used to store and track visits across websites.</td>
              <td>yes</td>
              <td>Bing</td>
              <td>persistent</td>
              <td>16 days</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>MUID</td>
              <td>Used to store and track visits across websites.</td>
              <td>No</td>
              <td>Bing</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>DSID</td>
              <td>
                Used for re-targeting, optimisation, reporting and attribution of online adverts.
              </td>
              <td>no</td>
              <td>DoubleClick</td>
              <td>persistent</td>
              <td>2 weeks</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>IDE</td>
              <td>
                Used to register and report the website user&apos;s actions after viewing or
                clicking one of the advertiser&apos;s ads with the purpose of measuring the efficacy
                of an ad and to present targeted ads to the user.
              </td>
              <td>no</td>
              <td>DoubleClick</td>
              <td>persistent</td>
              <td>2 weeks</td>
              <td>non essential</td>
            </tr>

            <tr>
              <td>m-ans_frontend_early_version</td>
              <td>
                Used to show and optimize advertisements to the same person across devices and
                browsers.
              </td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-b</td>
              <td>Used to determine the ad a user clicked on to arrive at our website.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-b_strict</td>
              <td>Used to determine the ad a user clicked on to arrive at our website.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-b_lax</td>
              <td>Used to determine the ad a user clicked on to arrive at our website.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-b_strict</td>
              <td>Used to determine the ad a user clicked on to arrive at our website.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-login_redirect_url</td>
              <td>
                Used to show and optimize advertisements to the same person across devices and
                browsers.
              </td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-s</td>
              <td>Used to authenticate your identity to Quora.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>3 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-sa</td>
              <td>Used to optimise ad delivery.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>3 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-tgvs</td>
              <td>Used to store activity to improve personalisation.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>3 years</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>m-uid</td>
              <td>Used to determine the ad a user clicked on to arrive at our website.</td>
              <td>no</td>
              <td>Quora</td>
              <td>persistent</td>
              <td>2 years</td>
              <td>non essential</td>
            </tr>

            <tr>
              <td>_clck</td>
              <td>
                Persists the Clarity User ID and preferences, unique to that site is attributed to
                the same user ID.
              </td>
              <td>yes</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>_clsk</td>
              <td>
                Connects multiple page views by a user into a single Clarity session recording.{' '}
              </td>
              <td>yes</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>1 day</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>CLID</td>
              <td>Identifies the first-time Clarity saw this user on any site using Clarity. </td>
              <td>no</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>ANONCHK</td>
              <td>
                Indicates whether MUID is transferred to ANID, a cookie used for advertising.
                Clarity doesn&apos;t use ANID and so this is always set to 0.{' '}
              </td>
              <td>no</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>10 mins</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>MR</td>
              <td>Indicates whether to refresh MUID. </td>
              <td>no</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>1 year</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>MUID</td>
              <td>
                Identifies unique web browsers visiting Microsoft sites. These cookies are used for
                advertising, site analytics, and other operational purposes.
              </td>
              <td>no</td>
              <td>Micrsoft Clarity</td>
              <td>persistent</td>
              <td>1 week</td>
              <td>non essential</td>
            </tr>
            <tr>
              <td>SM</td>
              <td>Used in synchronizing the MUID across Microsoft domains.</td>
              <td>no</td>
              <td>Micrsoft Clarity</td>
              <td>session</td>
              <td>1 min</td>
              <td>non essential</td>
            </tr>
          </tbody>
        </Table>
      </section>
      <section>
        <h2>6. How can you control cookies?</h2>
        <strong>Tools within this Cookie Notice</strong>
        <p>
          You can set and modify your cookies preferences at any time, by using the Cookie
          Management Preference Tool available at the bottom of this Cookies Policy.
        </p>
        <strong>Browser and devices controls</strong>
        <p>
          Some web browsers provide settings that allow you to control or reject cookies or to alert
          you when a cookie is placed on your computer. The procedure for managing cookies is
          slightly different for each internet browser. You can check the specific steps in your
          particular browser help menu.
        </p>
        <p>
          You also may be able to reset device identifiers by activating the appropriate setting on
          your mobile device. The procedure for managing device identifiers is slightly different
          for each device. You can check the specific steps in the help or settings menu of your
          particular device.
        </p>
        <strong>Social Cookies</strong>
        <p>
          To allow you to share content on social media, some features of this website use social
          media plug-ins. Depending on your social media account settings, we automatically receive
          information from the social media platform when you use the corresponding button on our
          website. To learn more about social media cookies, we suggest you refer to your social
          media platform&apos;s Cookie Notice and Privacy Notice.
        </p>
        <strong>Google Cookies</strong>
        <p>
          We use Google Analytics, which is a Google service that uses cookies and other data
          collection technologies to collect information about your use of the website and services
          in order to report website trends.
        </p>
        <p>
          You can opt out of Google Analytics by visiting{' '}
          <A to="https://www.google.com/settings/ads"> www.google.com/settings/ads</A> or by
          downloading the Google Analytics opt-out browser add-on at{' '}
          <A to="https://tools.google.com/dlpage/gaoptout">
            https://tools.google.com/dlpage/gaoptout
          </A>
          .
        </p>
      </section>
      <section>
        <h2>7. What happens if you disable all cookies?</h2>
        <p>
          The changes you make to your cookie preferences may make browsing our website a less
          satisfying experience. In some cases, you may even find yourself unable to use all or part
          of our site.
        </p>
      </section>

      <section>
        <h2>8. How to contact us?</h2>
        <p>
          If you have questions about this Cookie Notice, here&apos;s how you can contact us at:{' '}
          <A to="mailto:dpo@qmee.com">dpo@qmee.com</A>.
        </p>
      </section>
      <h1>Cookie Settings</h1>
      <p>You can withdraw consent at any time by switching off the relevant setting.</p>
      <SuspenseWithIndicator withoutSSR>
        <CookieSettings
          handleToggleSettingCookie={handleToggleSettingCookie}
          handleToggleMarketingCookie={handleToggleMarketingCookie}
          handleToggleTrackingCookie={handleToggleTrackingCookie}
          handleToggleSocialCookie={handleToggleSocialCookie}
          settingCookie={settingCookie}
          trackingCookie={trackingCookie}
          marketingCookie={marketingCookie}
          socialCookie={socialCookie}
        />
      </SuspenseWithIndicator>
    </CompanyInfoPage>
  );
};

export default CookieNotice;
